/**
 * Contains the (un)formatter for an IBAN
 * @module @aab/data-formatters
 * @name @aab/data-formatters/iban
 */
const IBAN_LENGTH = 18;

/**
   * Formats an IBAN.
   * @param {string}iban the bank account number which should be formatted
   * @param {string}separator which will be used to format the account number, default is a dot
   *
   * @example
   *  ibanFormat('NL00ABNA1234567890')      => 'NL00 ABNA 1234 5678 90'
   *  ibanFormat('NL00ABNA1234567890', '-') => 'NL00-ABNA-1234-5678-90'
   *  ibanFormat('Not an iban number', '.') => 'Not .an i.ban .numb.er' (will be handled as valid iban)
   *  ibanFormat('Not an iban')             => 'Not an iban'
   *  ibanFormat()                          => ''
   * @return {string} with formatted IBAN
   */
export function ibanFormat(iban = '', separator = ' ') {
  if (iban.length === IBAN_LENGTH) {
    return iban.replace(/(.{4})(?!$)/g, `$1${separator}`);
  }
  return iban;
}

/**
   * Removes any format from an IBAN.
   * @param {string}formattedIban the formatted bank account number
   *
   * @example
   *  ibanUnformat('NL00 ABNA 1234 5678 90') => 'NL00ABNA1234567890'
   *  ibanUnformat('NL00-ABNA-1234-5678-90') => 'NL00ABNA1234567890'
   *  ibanUnformat('Not .an i.ban .numb.er') => 'Not an iban number' (will be handled as valid iban)
   *  ibanUnformat('Not an iban')            => 'Not an iban'
   *  ibanUnformat()                         => ''
   * @return {string} with the IBAN
   */
export function ibanUnformat(formattedIban = '') {
  const separatorLength = formattedIban.length - IBAN_LENGTH;
  if (separatorLength > 0 && separatorLength % 4 === 0) {
    const separator = formattedIban.substr(4, separatorLength / 4);
    const replace = new RegExp(separator, 'g');
    return formattedIban.replace(replace, '');
  }
  return formattedIban;
}
